import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import client from "../api/client";
import { AUTH_USER_KEY, clearStoredAuth } from "../utils/authSession";
import "./Profile.css";

const emptyProfile = {
  name: "",
  surname: "",
  email: "",
  phone: "",
  address: "",
  location: "",
  postalCode: "",
  dateOfBirth: "",
  image: "",
  role: "",
};

const normalizeProfile = (user = {}) => ({
  name: user.name || "",
  surname: user.surname || "",
  email: user.email || "",
  phone: user.phone || "",
  address: user.address || "",
  location: user.location || "",
  postalCode: user.postal_code || user.postalCode || "",
  dateOfBirth: user.date_of_birth ? String(user.date_of_birth).slice(0, 10) : "",
  image: user.profile_image || user.image || "",
  role: user.role || "",
});

export default function Profile() {
  const navigate = useNavigate();
  const storedUserRef = useRef(JSON.parse(localStorage.getItem(AUTH_USER_KEY) || "{}"));
  const storedUser = storedUserRef.current;
  const userId = storedUser.id;
  const [profile, setProfile] = useState(() => ({
    ...emptyProfile,
    ...normalizeProfile(storedUser),
  }));
  const [saving, setSaving] = useState(false);

  const updateStoredUser = useCallback((profileData) => {
    const nextUser = {
      ...storedUserRef.current,
      name: profileData.name,
      surname: profileData.surname,
      email: profileData.email,
      phone: profileData.phone,
      address: profileData.address,
      location: profileData.location,
      postalCode: profileData.postalCode,
      postal_code: profileData.postalCode,
      date_of_birth: profileData.dateOfBirth,
      image: profileData.image,
      profile_image: profileData.image,
      role: profileData.role,
    };

    storedUserRef.current = nextUser;
    localStorage.setItem(AUTH_USER_KEY, JSON.stringify(nextUser));
  }, []);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!userId) return;

      try {
        const res = await client.get("/users");
        const latestUser = (res.data || []).find((item) => String(item.id) === String(userId));
        if (!latestUser) return;

        const nextProfile = {
          ...emptyProfile,
          ...normalizeProfile({
            ...storedUserRef.current,
            ...latestUser,
          }),
        };

        setProfile(nextProfile);
        updateStoredUser(nextProfile);
      } catch (err) {
        console.error("Fetch profile error:", err);
      }
    };

    fetchProfile();
  }, [updateStoredUser, userId]);

  const updateProfileField = (field, value) => {
    setProfile((prev) => ({ ...prev, [field]: value }));
  };

  const handleLogout = () => {
    clearStoredAuth();
    navigate("/login");
  };

  const handlePhoto = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      alert("Please select a valid image file.");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      updateProfileField("image", reader.result || "");
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (!userId) {
      alert("Unable to save profile. Please login again.");
      return;
    }

    if (!profile.name || !profile.email) {
      alert("Name and email are required.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: profile.name,
        surname: profile.surname,
        email: profile.email,
        role: profile.role || storedUser.role || "Employee",
        phone: profile.phone,
        address: profile.address,
        location: profile.location,
        postal_code: profile.postalCode,
        date_of_birth: profile.dateOfBirth || null,
        profile_image: profile.image,
      };
      const res = await client.put(`/users/${userId}`, payload, {
        headers: { "Content-Type": "application/json" },
      });
      const savedProfile = {
        ...profile,
        ...normalizeProfile({
          ...profile,
          ...(res.data?.user || {}),
        }),
      };

      setProfile(savedProfile);
      updateStoredUser(savedProfile);
      alert("Profile updated successfully!");
    } catch (err) {
      console.error("Save profile error:", err);
      alert(err.response?.data?.message || "Failed to update profile.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="settings-page">
      <div className="settings-sidebar card">
        <div className="profile-box">
          {profile.image ? (
            <img className="profile-avatar" src={profile.image} alt="avatar" />
          ) : (
            <i className="fa fa-user-circle profile-avatar profile-avatar-icon" aria-hidden="true"></i>
          )}
          <h3>{`${profile.name} ${profile.surname}`.trim()}</h3>
          <p className="role-tag">{profile.role}</p>
          <p className="email-tag">{profile.email}</p>
          <label className="photo-button">
            Change Photo
            <input type="file" accept="image/*" onChange={handlePhoto} />
          </label>
        </div>
        <ul className="menu">
          <li className="active">Personal Information</li>
          <li>Login &amp; Password</li>
          <li className="logout" onClick={handleLogout}>
            Log Out
          </li>
        </ul>
      </div>

      <div className="settings-content card">
        <h2>Personal Information</h2>
        <div className="form-grid">
          <div>
            <label>First Name</label>
            <input value={profile.name} onChange={(e) => updateProfileField("name", e.target.value)} />
          </div>
          <div>
            <label>Last Name</label>
            <input value={profile.surname} onChange={(e) => updateProfileField("surname", e.target.value)} />
          </div>
          <div className="full">
            <label>Email</label>
            <input value={profile.email} disabled />
          </div>
          <div className="full">
            <label>Phone</label>
            <input value={profile.phone} onChange={(e) => updateProfileField("phone", e.target.value)} />
          </div>
          <div className="full">
            <label>Address</label>
            <input value={profile.address} onChange={(e) => updateProfileField("address", e.target.value)} />
          </div>
          <div>
            <label>Date of Birth</label>
            <input
              type="date"
              value={profile.dateOfBirth}
              onChange={(e) => updateProfileField("dateOfBirth", e.target.value)}
            />
          </div>
          <div>
            <label>Location</label>
            <input value={profile.location} onChange={(e) => updateProfileField("location", e.target.value)} />
          </div>
          <div>
            <label>Postal Code</label>
            <input value={profile.postalCode} onChange={(e) => updateProfileField("postalCode", e.target.value)} />
          </div>
        </div>
        <div className="actions">
          <button className="save" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
